import React from 'react';
import { connect } from 'react-redux';
import { selectEntityOptions, selectOptionsLoading } from '../../redux/optionsSlice';
import CheckboxGroup from '../CheckboxGroup';
import RadioGroup from '../RadioGroup';
import TripAdvisorInput from './TripAdvisorInput';
import MultiSelectControl from '../MultiSelectControl';
import CustomSelect from '../inputs/CustomSelect';

import { evaluateCondition } from '../../utils/utils';

import { DATA_TYPE_STRING, DATA_TYPE_INTEGER,
  INPUT_LAYOUT_TYPE_SEPARATE_LABEL, INPUT_LAYOUT_TYPE_PLACEHOLDER, 
  INPUT_TYPE_CHECKBOX, INPUT_TYPE_SELECT_MULTI, INPUT_TYPE_SELECT_SINGLE,
  INPUT_TYPE_TEXT, INPUT_TYPE_TEXT_AREA, INPUT_TYPE_FILE, INPUT_TYPE_RADIO,
  INPUT_TYPE_RADIO_GROUP, INPUT_TYPE_SEARCH_FIELD, INPUT_TYPE_RANGE,
  INPUT_TYPE_TRIPADVISOR, INPUT_TYPE_HIDDEN
} from '../componentConstants';
import InputSearch from '../inputs/InputSearch';

class InputControl extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showError: false,
      optionsLoading: false,
      fileValue: null
    };
    this.fileInputRef = React.createRef(null);
    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleIconMouseEnter = this.handleIconMouseEnter.bind(this);
    this.handleIconMouseLeave = this.handleIconMouseLeave.bind(this);
    this.handleIconClick = this.handleIconClick.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.getFormFieldId = this.getFormFieldId.bind(this);
  }
  
  handleFileChange(event) {
    const files = event.target.files;
    this.setState({ fileValue: files });
    this.props.onChange(files);
  }

  handleChange(event) {
    let newValue;
    const { input, onChange } = this.props;

    // CheckboxGroup calls onChange directly, so this handler only needs to manage single checkboxes and other input types.
    if (input.inputType === INPUT_TYPE_CHECKBOX && !(input?.options?.length > 1)) {
      newValue = event.target.checked;
    }
    else if (input.inputType === INPUT_TYPE_SELECT_MULTI) {
      newValue = Array.from(event.target.selectedOptions, (option) => option.value);
    }
    else if (input.inputType === INPUT_TYPE_SELECT_SINGLE) {
      newValue = input.dataType === DATA_TYPE_STRING ? event.target.value : !!event.target.value ? parseInt(event.target.value) : null;
    }
    else if (input.inputType === INPUT_TYPE_FILE) {
      return;
    }
    else {
      newValue = event.target.value;
    }
    if (onChange) onChange(newValue);
  }

  handleRangeChange(event, part) {
    const { value: currentValue, onChange } = this.props;
    const newValue = { ...(currentValue || { start: '', end: '' }) };
    newValue[part] = event.target.value;

    if (onChange) {
      // Ensure both parts exist, even if empty, before calling onChange
      onChange({
        start: newValue.start ?? '',
        end: newValue.end ?? '',
      });
    }
  }

  handleBlur(event) {
    if(!!this.props.onBlur) {
      this.props.onBlur(event.target.name, event.target.value);
    }
  }

  handleIconMouseEnter() {
    this.setState({ showError: true });
  }

  handleIconMouseLeave() {
    this.setState({ showError: false });
  }

  handleIconClick() {
    this.setState((prevState) => ({ showError: !prevState.showError }));
  }

  getFormFieldId() {
    const { input, formId } = this.props;
    return input.fieldName + '_' + (formId ?? '');
  }

  render() {
    const { input, value, error, record, cachedData } = this.props;
    const { showError } = this.state;

    const inputProps = {
      id: this.getFormFieldId(),
      name: input.fieldName,
      placeholder: input.layoutType === INPUT_LAYOUT_TYPE_PLACEHOLDER ? input.label : input.placeholder ?? '',
      title: input.tooltipText ?? '',
      onChange: this.handleChange,
      onClick: input.onClick,
      onBlur: this.handleBlur,
      disabled: evaluateCondition(input.isDisabled, record),
      required: evaluateCondition(input.isRequired, record),
      className: `${error ? ' validation-error' : ''}`,
    };

    let inputElement;

    switch (input.inputType) {
      case INPUT_TYPE_TEXT_AREA:
        inputElement = <textarea {...inputProps} value={value || ''} />;
        break;
      case INPUT_TYPE_CHECKBOX:
        if (input?.options?.length > 1) {
          inputElement = (
            <CheckboxGroup
              groupLabel={input.label}
              options={input.options ?? cachedData}
              value={value || []}
              onChange={this.props.onChange}
              inline={input.layoutType !== INPUT_LAYOUT_TYPE_SEPARATE_LABEL}
            />
          );
        }
        else {
          inputElement = <input type={INPUT_TYPE_CHECKBOX} {...inputProps} checked={!!value} />;
        }
        break;
      case INPUT_TYPE_RADIO:
        if (input?.options?.length > 1) {
          inputElement = (
            <RadioGroup
              groupLabel={input.label}
              options={input.options ?? cachedData}
              value={value}
              onChange={this.props.onChange}
              inline={input.layoutType !== INPUT_LAYOUT_TYPE_SEPARATE_LABEL}
            />
          );
        }
        else {
          inputElement = <input type={INPUT_TYPE_RADIO} {...inputProps} checked={!!value} />;
        }
        break;
      case INPUT_TYPE_RADIO_GROUP:
        inputElement = (
          <RadioGroup
              groupLabel={input.label}
              options={input.options ?? cachedData}
              value={value}
              onChange={this.props.onChange}
              inline={input.inline !== undefined ? input.inline : input.layoutType !== INPUT_LAYOUT_TYPE_SEPARATE_LABEL}
          />
        );
        break;
      case INPUT_TYPE_SELECT_SINGLE:
        const isLoading = !!input?.options?.length ? false : this.props[`${input.optionsEntityName}_${input.fieldName}_loading`];
        if(isLoading) inputProps.disabled = true;
        const options = input?.options ?? cachedData;
        const showEmptyOption = this.props.input?.showEmptyListOption !== false;
        
        const hasDescriptions = options.some(opt => opt.description);
        
        if (hasDescriptions) {
          inputElement = (
            <CustomSelect
              {...inputProps}
              options={options}
              value={value || ''}
              showEmptyOption={showEmptyOption}
              emptyOptionText={isLoading ? 'Loading options...' : `Choose ${!!input?.label ? 'a ' + input?.label?.toLowerCase() : 'one option'}`}
              searchable={options.length > 10}
              className={`${isLoading ? 'is-loading' : ''}`}
            />
          );
        } else {
          inputElement = (
            <div className={`select-wrapper ${isLoading ? 'is-loading' : ''}`}>
              <select {...inputProps} value={value || ''} className={isLoading ? 'with-spinner' : ''}>
                {showEmptyOption && (
                  <option value="">
                    {isLoading ? 'Loading options...' : `Choose ${!!input?.label ? 'a ' + input?.label?.toLowerCase() : 'one option'}`}
                  </option>
                )}
                {!isLoading && options.map((option) => {
                  const value = option.value ?? option.label ?? option;
                  const label = option.label ?? option;
                  return (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                })}
              </select>
              {isLoading && (
                <div className="spinner">
                  <div className="spinner-inner"></div>
                </div>
              )}
            </div>
          );
        }
        break;
      case INPUT_TYPE_SELECT_MULTI:
      inputElement = (
        <MultiSelectControl
          key={JSON.stringify(value || [])}
          {...inputProps}
          options={input.options ?? cachedData}
          value={value || []}
          onChange={this.props.onChange}
          onSearch={this.props.onSearch}
        />
      );
        break;
      case INPUT_TYPE_FILE:
        inputElement = (
          <input type="file" {...inputProps} ref={this.fileInputRef}
            onChange={this.handleFileChange} accept={input.acceptedFileTypes}
          />
        );
        break;
      case INPUT_TYPE_RANGE:
        const rangeValue = value || { start: '', end: '' };
        const rangeDataType = input.dataType || DATA_TYPE_INTEGER;
        inputElement = (
          <div className="range">
            <input
              type={rangeDataType === DATA_TYPE_INTEGER ? 'number' : 'text'}
              {...inputProps}
              id={`${input.fieldName}-start`}
              name={`${input.fieldName}-start`}
              value={rangeValue.start}
              onChange={(e) => this.handleRangeChange(e, 'start')}
              placeholder={input.layoutType === INPUT_LAYOUT_TYPE_PLACEHOLDER ? (input.placeholderStart || 'Start') : ''}
              className={`start${error ? ' validation-error' : ''}`}
            />
            <span className="separator">-</span>
            <input
              type={rangeDataType === DATA_TYPE_INTEGER ? 'number' : 'text'}
              {...inputProps}
              id={`${input.fieldName}-end`}
              name={`${input.fieldName}-end`}
              value={rangeValue.end}
              onChange={(e) => this.handleRangeChange(e, 'end')}
              placeholder={input.layoutType === INPUT_LAYOUT_TYPE_PLACEHOLDER ? (input.placeholderEnd || 'End') : ''}
              className={`end${error ? ' validation-error' : ''}`}
            />
          </div>
        );
        break;
      case INPUT_TYPE_SEARCH_FIELD:
        inputElement = (
          <InputSearch
            {...inputProps}
            value={value || ''}
            error={error}
            onSearch={input.onSearch}
          />
        );
        break;
      case INPUT_TYPE_TRIPADVISOR:
        inputElement = (
          <TripAdvisorInput
            input={input}
            record={record}
            value={value}
            onChange={this.props.onChange}
            onFieldError={this.props.onFieldError}
            forceRecordUpdate={this.props.forceRecordUpdate}
            onBlur={this.props.onBlur}
            layoutType={input.layoutType}
            label={input.label}
            isDisabled={inputProps.disabled}
            error={error}
            initialSearchTerm={input.initialSearchTerm}
          />
        );
        break;
      case INPUT_TYPE_HIDDEN:
        return null;
      default:
        inputElement = <input type={input.inputType || INPUT_TYPE_TEXT} {...inputProps} value={value || ''} />;
    }

    
    return (
      !evaluateCondition(input.isHidden, record) && (
        <div
          className={`input-control ${
            input.layoutType === INPUT_LAYOUT_TYPE_SEPARATE_LABEL ? 'separate-label' : 'inline'
          }`}
        >
          {/* Conditionally render the main label. Don't render if it's a multi-checkbox group or multi-radio group (handled internally) or placeholder layout or TripAdvisor input */}
          {input.layoutType !== INPUT_LAYOUT_TYPE_PLACEHOLDER && !((input.inputType === INPUT_TYPE_CHECKBOX || input.inputType === INPUT_TYPE_RADIO) && input?.options?.length > 1) && input.inputType !== INPUT_TYPE_TRIPADVISOR && (
            <label htmlFor={this.getFormFieldId()}>
              {input.label}
              {!!input.label && (input?.validationRules?.isRequired || evaluateCondition(input.isRequired, record)) ? (
                <span className="required">*</span>
              ) : null}
             </label>
           )}
           {inputElement}
           {input.helpText && (
             <div className="input-help-text">
               {typeof input.helpText === 'function' ? input.helpText(record) : input.helpText}
             </div>
           )}
           {error && input.inputType !== INPUT_TYPE_TRIPADVISOR && (
             <div
               className="validation-error-icon"
              onMouseEnter={this.handleIconMouseEnter}
              onMouseLeave={this.handleIconMouseLeave}
              onClick={this.handleIconClick}
            >
              <span className="validation-error-icon-inner">!</span>
              {
                <div className={`validation-error-message${showError ? ' visible' : ''}`}>
                  <span>{error?.shortMessage ?? ''}</span>
                </div>
              }
            </div>
          )}
        </div>
      )
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const cachedData = selectEntityOptions(
    state,
    ownProps.input?.optionsEntityName + '.' + ownProps.input?.fieldName,
    ownProps.cacheKey
  ) ?? [];
  
  return {
    cachedData,
    [`${ownProps.input?.optionsEntityName}_${ownProps.input?.fieldName}_loading`]: 
      selectOptionsLoading(state, ownProps.input?.optionsEntityName, ownProps.input?.fieldName)
  };
};

export default connect(mapStateToProps)(InputControl);
